import qs from 'qs';
import { useCallback, useMemo, useRef } from 'react';
import { useEffectOnce } from 'usehooks-ts';
import { useHistory, useLocation } from 'react-router-dom';

export function getLimitAndOffsetFromPageParams(page: number, pageSize: number) {
  return {
    limit: pageSize,
    offset: page * pageSize,
  };
}

export function parseQueryParamAsBoolean(
  oneOrZero: string | qs.ParsedQs | string[] | qs.ParsedQs[] | undefined,
  defaultValue?: boolean,
): boolean {
  const parsedNumber = Number(parseQueryParamAsString(oneOrZero));
  return Number.isNaN(parsedNumber) ? defaultValue || false : !!parsedNumber;
}

export function parseQueryParamAsArray(
  queryParam: string | qs.ParsedQs | string[] | qs.ParsedQs[] | undefined,
) {
  if (typeof queryParam === 'string' && queryParam.length > 0) {
    return queryParam.split(',');
  }
  return Array.isArray(queryParam) ? queryParam : [];
}

export function parseQueryParamAsString(
  queryParam: string | qs.ParsedQs | string[] | qs.ParsedQs[] | undefined,
) {
  return typeof queryParam === 'string' ? queryParam : '';
}

export function parseQueryParamAsNumber(
  queryParam: string | qs.ParsedQs | string[] | qs.ParsedQs[] | undefined,
) {
  return Number.isNaN(Number(queryParam)) ? null : Number(queryParam);
}

export function useQueryParams(initialParams?: { [key: string]: string }) {
  const location = useLocation();
  const history = useHistory();

  // Ref so updateQueryParams always calls the current history instance without
  // needing it as a dep (which would recreate the callback on every navigation).
  const historyRef = useRef(history);
  historyRef.current = history;

  const updateQueryParams = useCallback((newParams: { [key: string]: any }, push = false) => {
    // window.location is updated synchronously by history.replace/push, so it
    // stays correct even when multiple effects call updateQueryParams in the same
    // render cycle. A locationRef would only reflect the pre-render snapshot and
    // would cause later same-cycle calls to overwrite changes from earlier ones.
    const params = qs.parse(window.location.search, { ignoreQueryPrefix: true });
    const mergedParams = {
      ...params,
      ...newParams,
    };
    Object.keys(mergedParams).forEach((key) => {
      if (mergedParams[key] == null) delete mergedParams[key];
    });
    const newQueryString = qs.stringify(mergedParams);
    const newURL = `${window.location.pathname}?${newQueryString}`;
    if (push) {
      historyRef.current.push(newURL);
    } else {
      historyRef.current.replace(newURL);
    }
  }, []);

  useEffectOnce(() => {
    if (initialParams) {
      updateQueryParams(initialParams);
    }
  });

  const queryParams = useMemo(() => {
    return qs.parse(location.search, { ignoreQueryPrefix: true }) as {
      [key: string]: string;
    };
  }, [location.search]);

  return {
    queryParams,
    updateQueryParams,
  };
}
